import React from 'react'
import PropTypes from 'prop-types'

import styled, { css } from 'styled-components'

import { Link as RouteLink } from 'react-router-dom'

import MuiPaper from '@material-ui/core/Paper'

import CsgFlex from 'components/atoms/Flex'
import CsgButton from 'components/atoms/Button'
import CsgHomeValue from 'components/atoms/HomeValue'
import CsgTypography from 'components/atoms/Typography'
import CsgIconHelpToolTip from 'components/molecules/IconHelpToolTip'

import theme from 'app/theme'
import config from 'app/config'

const sizes = ['small', 'large']

const Paper = ({ alignItems, size, ...props }) => <MuiPaper {...props} />

const HomePaper = styled(Paper)`
  && {
    flex: 1;
    box-shadow: 0px 6px 10px rgba(0, 0, 0, 0.05);
    justify-content: space-between;
    display: flex;
    flex-direction: column;
    align-items: ${props => props.alignItems};

    ${props =>
      props.size &&
      css`
        height: ${config.cardHeight * (sizes.indexOf(props.size) + 1)}px;
      `}
  }
`

const Typography = ({ action, ...props }) => <CsgTypography {...props} />

const Title = styled(Typography)`
  && {
    font-size: 18px;
    white-space: normal;
    ${props =>
      props.action &&
      css`
        font-size: 14px;
        color: ${theme.palette.secondary.light};
        cursor: pointer;
      `};
  }
`

const Link = styled(RouteLink)`
  && {
    text-decoration: none;
  }
`

const Header = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: ${theme.spacing(2)}px;
  padding-left: ${theme.spacing(2)}px;
  padding-right: ${theme.spacing(2)}px;
`

const Footer = styled.div`
  width: 100%;
  padding-top: 0 !important;
  padding: ${theme.spacing(2)}px;

  ${theme.breakpoints.down('md')} {
    padding: ${theme.spacing(2)}px;
  }
`

const HeaderTitle = styled(CsgFlex)`
  && {
    > svg {
      margin-right: ${theme.spacing(2)}px;
    }
  }
`

const Button = styled(({ textColor, ...props }) => <CsgButton {...props} />)`
  && {
    font-size: 0.75rem;
    color: ${props => props.textColor && theme.palette[props.textColor].main};
    border-color: ${theme.palette.grey[400]};
    border-radius: 19px;

    &:before {
      content: '';
      margin-right: ${theme.spacing(2) - 4}px;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background-color: ${props => props.textColor && theme.palette[props.textColor].main};
    }
  }
`

const Body = styled.div`
  flex: 1;
  width: 100%;

  ${props =>
    props.padding &&
    css`
      padding: 0 ${theme.spacing(2)}px;
      padding-bottom: ${theme.spacing(2)}px;

      ${theme.breakpoints.down('md')} {
        padding: 0 ${theme.spacing(2)}px;
        padding-bottom: ${theme.spacing(2)}px;
      }
    `};
`

const Action = ({ action }) => (
  <>
    {action && (
      <Title variant="subtitle2" color="secondary" action noWrap>
        {typeof action === 'string' ? action.toUpperCase() : action}
      </Title>
    )}
  </>
)

const Card = ({
  icon,
  title,
  padding,
  action,
  to,
  color,
  value,
  description,
  children,
  textButton,
  onClick,
  containerStyle,
  tooltipTitle,
  ...props
}) => {
  return (
    <HomePaper elevation={1} {...props} style={containerStyle}>
      {title && (
        <Header>
          <HeaderTitle alignItems="center">
            {icon}

            <Title variant="h6" color="secondary" noWrap>
              {title}
            </Title>
            {tooltipTitle && (
              <CsgIconHelpToolTip
                padding="0 0 0 8px"
                iconType="info"
                maxWidth="250"
                placement="right"
                title={tooltipTitle}
              />
            )}
          </HeaderTitle>

          {to ? (
            <Link to={to}>
              <Action action={action} />
            </Link>
          ) : (
            <Action action={action} />
          )}
        </Header>
      )}

      <Body padding={padding}>
        {value !== undefined && (
          <CsgHomeValue color={color} description={description}>
            {value}
          </CsgHomeValue>
        )}

        {children}
      </Body>

      {textButton && (
        <Footer>
          <Button onClick={onClick} variant="outlined" textColor={color}>
            {textButton}
          </Button>
        </Footer>
      )}
    </HomePaper>
  )
}

Card.defaultProps = {
  padding: true,
  alignItems: 'flex-start',
  containerStyle: {}
}

Card.propTypes = {
  ...Card.propTypes,
  icon: PropTypes.node,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  action: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  to: PropTypes.string,
  color: PropTypes.string,
  containerStyle: PropTypes.object,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  description: PropTypes.string,
  textButton: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  alignItems: PropTypes.oneOf(['flex-start', 'center', 'flex-end', 'stretch', 'baseline']),
  size: PropTypes.oneOf(sizes),
  padding: PropTypes.bool,
  tooltipTitle: PropTypes.string
}

export default Card
